<template>
  <template v-if="!isCollapsed">
    <transition name="slide">
      <div class="filter-box">
        <div ref="htmlRef" class="filter-container">
          <DashboardDropdownFilterComplex
            :complexes="complexes"
            :selectedComplex="filters.current.complex"
            :optionsWithData="undefined"
            @selectionChanged="complexSelected"
          />
          <DashboardDropdownFilterBuilding
            :buildings="filteredBuildings"
            :selectedBuildings="filters.current.buildings"
            :presentAsMultiSelect="!!filters.current.complex"
            :optionsWithData="undefined"
            @selectionChanged="(buildings) => filters.$patch({ current: { buildings } })"
          />
          <DashboardDropdownFilterProjectType
            type="type"
            :projectTypes="filteredProject"
            :selectedProjectTypes="filters.current.projectTypes"
            :presentAsMultiSelect="!!filters.current.complex"
            :optionsWithData="undefined"
            @selectionChanged="(projectTypes) => filters.$patch({ current: { projectTypes } })"
          />
          <DashboardDropdownFilterProjectType
            type="status"
            :projectTypes="filteredStatuses"
            :selectedProjectTypes="filters.current.projectStatuses"
            :presentAsMultiSelect="!!filters.current.complex"
            :optionsWithData="undefined"
            @selectionChanged="
              (projectStatuses) => filters.$patch({ current: { projectStatuses } })
            "
          />
          <MinMaxSliderFilter
            v-if="filters.current.type === FeatureType.ProjectAnalysisInternal"
            :from="filters.current.budget?.from ?? 0"
            :to="filters.current.budget?.to ?? 5000"
            :min="0"
            :max="5000"
            :step="1"
            :format="{ format: { type: 'format-string', format: '%s tkr' } }"
            fromColor="#e0e0e0"
            toColor="#e0e0e0"
            middleColor="#3f46d6"
            label="Budget (tkr)"
            labelType="paragraph"
            @updateValue="updateBudget"
          />
        </div>
        <div class="button-row">
          <ButtonOutline
            size="small"
            :buttonText="t('general__text__dashboard_reset_selections')"
            @onClicked="resetForm"
          />
          <ButtonPrimary
            size="small"
            :disabled="filters.isCurrentFilterSubmitted"
            :buttonText="t('general__text__dashboard_update_selections')"
            @onClicked="$emit('submitForm')"
          />
        </div>
      </div>
    </transition>
  </template>
</template>
<script lang="ts" setup>
import {
  DashboardDropdownFilterBuilding,
  DashboardDropdownFilterComplex,
  DashboardDropdownFilterProjectType
} from './filters'
import { ComplexProperties, BuildingProperties, ComplexFeature } from '../../types'

import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import { ref, watch, computed, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { InformationResponse } from '@/api-client/models/utilization/Contracts'
import { FeatureType, useFilters, useCurrentFeatureFilters } from '@/composables/useFilters'
import {
  EXTERNAL_PROJECT_STATUSES,
  EXTERNAL_PROJECT_TYPES,
  PROJECT_STATUSES,
  PROJECT_TYPES
} from '@/api-client/models/project/Contracts'
import { PROJECT_STATUS, PROJECT_TYPE } from '@/api-client/models/project/Constants'
import MinMaxSliderFilter from '@/design-system/molecules/MinMaxSliderFilter.vue'

type Props = {
  information: InformationResponse | undefined
  isCollapsed: boolean
}
type Emits = {
  (e: 'resetForm'): void
  (e: 'submitForm'): void
}
const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const vuex = useStore()

const currentFeature = useCurrentFeatureFilters()
const filterType = computed(
  (): FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal => {
    if (!currentFeature.value) {
      return FeatureType.ProjectAnalysisExternal
    }
    return currentFeature.value.submitted.type as
      | FeatureType.ProjectAnalysisExternal
      | FeatureType.ProjectAnalysisInternal
  }
)
const filters = useFilters<
  FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal
>(filterType.value)
const { t, locale } = useI18n()

const htmlRef = ref<HTMLElement | null>(null)
const complexes = computed<ComplexProperties[]>(() =>
  vuex.getters.allComplexes.map((x: ComplexFeature) => x.properties)
)
const buildings = computed<BuildingProperties[]>(() =>
  vuex.getters.allBuildings.map((x: ComplexFeature) => x.properties)
)
const filteredBuildings = computed<BuildingProperties[]>(() => {
  if (!filters.current.complex) {
    return buildings.value
  }
  const id = filters.current.complex.belongsToId
  return buildings.value.filter((x) => x.complexId === id)
})
// Need to change to project
const filteredProject = computed(() => {
  if (filters.current.type === FeatureType.ProjectAnalysisInternal) {
    return PROJECT_TYPES.map((x) => ({
      id: x,
      title: PROJECT_TYPE[x][locale.value]
    }))
  }
  return EXTERNAL_PROJECT_TYPES.map((x) => ({
    id: x,
    title: PROJECT_TYPE[x][locale.value]
  }))
})

const filteredStatuses = computed(() => {
  if (filters.current.type === FeatureType.ProjectAnalysisInternal) {
    return PROJECT_STATUSES.map((x) => ({
      id: x,
      title: PROJECT_STATUS[x][locale.value]
    }))
  }
  return EXTERNAL_PROJECT_STATUSES.map((x) => ({
    id: x,
    title: PROJECT_STATUS[x][locale.value]
  }))
})

watch(
  () => props.isCollapsed,
  async () => {
    await nextTick()
    htmlRef.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }
)
watch(
  () => filters.current.buildings,
  (values) => {
    if (!filters.current.complex && values.length === 1) {
      updateComplexSelection(values)
    }
  }
)

function resetForm() {
  emits('resetForm')
}

function complexSelected(complex: ComplexProperties | undefined) {
  filters.$patch({
    current: {
      complex: complex,
      buildings: [],
      projectTypes: [],
      projectStatuses: []
    }
  })
}

function updateComplexSelection(buildings: BuildingProperties[]) {
  const complex = complexes.value.find((x) => x.belongsToId === buildings[0].complexId)
  filters.$patch({ current: { complex: complex } })
}

function updateBudget(value: { from: number; to: number }) {
  filters.$patch({ current: { budget: value } })
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.filter-box {
  background-color: white;
  margin-left: -24px;
  margin-right: -24px;
  padding-left: 24px;
  padding-right: 24px;
  border-bottom: 1px solid #e0e0e0;
}
.filter-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: variables.$gap-large;
  padding: 16px 0;
}
.button-row {
  display: flex;
  gap: variables.$gap-large;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding-bottom: 16px;
}
.bookable-filter {
  padding-top: 4px;
}
// Sliding animation
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease-in-out;
}

.slide-enter,
.slide-leave-to {
  transform: translateY(-100%);
  opacity: 1;
}
</style>
